/**
 * Hooks TanStack Query para CxC/CxP.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import apiClient from '../../../lib/apiClient'

// ─── Types ──────────────────────────────────────────────────────────────────

export interface PaymentTerm {
  id: string
  name: string
  code: string
  days: number
  description: string
  is_default: boolean
  is_active: boolean
  created_at: string
}

export interface AgingSummary {
  current: string
  '1_30': string
  '31_60': string
  '61_90': string
  over_90: string
  total: string
}

export interface AgingCustomer {
  customer_id: string
  customer_name: string
  customer_tax_id: string
  current: string
  '1_30': string
  '31_60': string
  '61_90': string
  over_90: string
  total: string
}

export interface AgingResult {
  as_of_date: string
  summary: AgingSummary
  by_customer: AgingCustomer[]
  details: any[]
  count: number
}

export interface DsoResult {
  // `null` cuando no hay con qué calcularlo: un mes sin ventas. NO es cero —
  // un DSO de 0 días se lee como «se cobra al contado», que es lo contrario.
  current_dso: string | null
  // El período del que sale `current_dso`: el último mes CERRADO, no el actual.
  current_period: string | null
  trend: {
    period: string; sales: string; ar_balance: string
    dso: string | null
    // El mes en curso: su denominador son las ventas de los días transcurridos
    // y el numerador el saldo completo, así que no es comparable con los meses
    // cerrados.
    partial?: boolean
  }[]
}

export interface CashFlowResult {
  weeks: { week_start: string; week_end: string; inflow: string; outflow: string; net: string }[]
  total_inflow: string
  total_outflow: string
  total_net: string
}

export interface StatementEntry {
  date: string
  type: string
  ref: string
  description: string
  debit: string
  credit: string
  balance: string
}

export interface StatementResult {
  customer_id?: string
  supplier_id?: string
  date_from: string
  date_to: string
  entries: StatementEntry[]
  total_debit: string
  total_credit: string
  closing_balance: string
}

export interface OverdueInvoice {
  document_id?: string
  invoice_id?: string
  document_type?: string
  invoice_number?: string
  customer_name?: string
  supplier_name?: string
  customer_tax_id?: string
  supplier_tax_id?: string
  issue_date: string
  due_date: string
  total: string
  days_overdue: number
}

export interface CollectionReminder {
  id: string
  customer: string
  customer_name: string
  customer_tax_id: string
  customer_email: string
  sale_document: string
  document_type: string
  document_folio: string
  document_total: string
  document_issue_date: string
  document_due_date: string
  reminder_type: string
  reminder_type_display: string
  channel: string
  channel_display: string
  status: string
  sent_at: string
  sent_by: string | null
  sent_by_name: string
  notes: string
}

// ─── Payment Terms ──────────────────────────────────────────────────────────

export function usePaymentTerms(params?: { active?: string }) {
  return useQuery({
    queryKey: ['payment-terms', params],
    queryFn: async () => {
      const res = await apiClient.get('/finance/payment-terms/', { params })
      return res.data as { payment_terms: PaymentTerm[]; count: number }
    },
  })
}

export function useCreatePaymentTerm() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PaymentTerm>) => {
      const res = await apiClient.post('/finance/payment-terms/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-terms'] }),
  })
}

export function useUpdatePaymentTerm(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<PaymentTerm>) => {
      const res = await apiClient.patch(`/finance/payment-terms/${id}/`, data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['payment-terms'] }),
  })
}

// ─── Receivables (CxC) ─────────────────────────────────────────────────────

export function useArAging(params?: { as_of_date?: string; customer_id?: string }) {
  return useQuery({
    queryKey: ['ar-aging', params],
    queryFn: async () => {
      const res = await apiClient.get('/finance/receivables/aging/', { params })
      return res.data as AgingResult
    },
  })
}

export function useDso(months = 12) {
  return useQuery({
    queryKey: ['dso', months],
    queryFn: async () => {
      const res = await apiClient.get('/finance/receivables/dso/', { params: { months } })
      return res.data as DsoResult
    },
  })
}

export function useCustomerStatement(customerId: string, params?: { date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['customer-statement', customerId, params],
    queryFn: async () => {
      const res = await apiClient.get(`/finance/receivables/statement/${customerId}/`, { params })
      return res.data as StatementResult
    },
    enabled: !!customerId,
  })
}

export function useArCashFlow(weeks = 12) {
  return useQuery({
    queryKey: ['ar-cash-flow', weeks],
    queryFn: async () => {
      const res = await apiClient.get('/finance/receivables/cash-flow/', { params: { weeks } })
      return res.data as CashFlowResult
    },
  })
}

export function useArOverdue() {
  return useQuery({
    queryKey: ['ar-overdue'],
    queryFn: async () => {
      const res = await apiClient.get('/finance/receivables/overdue/')
      return res.data as { invoices: OverdueInvoice[]; count: number }
    },
  })
}

export function useCollectionReminders(params?: { customer_id?: string; document_id?: string }) {
  return useQuery({
    queryKey: ['collection-reminders', params],
    queryFn: async () => {
      const res = await apiClient.get('/finance/receivables/reminders/', { params })
      return res.data as { reminders: CollectionReminder[]; count: number }
    },
  })
}

export function useCreateReminder() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: Partial<CollectionReminder>) => {
      const res = await apiClient.post('/finance/receivables/reminders/', data)
      return res.data
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['collection-reminders'] }),
  })
}

// ─── Payables (CxP) ────────────────────────────────────────────────────────

export function useApAging(params?: { as_of_date?: string; supplier_id?: string }) {
  return useQuery({
    queryKey: ['ap-aging', params],
    queryFn: async () => {
      const res = await apiClient.get('/finance/payables/aging/', { params })
      return res.data
    },
  })
}

export function useDpo(months = 12) {
  return useQuery({
    queryKey: ['dpo', months],
    queryFn: async () => {
      const res = await apiClient.get('/finance/payables/dpo/', { params: { months } })
      return res.data
    },
  })
}

export function useSupplierStatement(supplierId: string, params?: { date_from?: string; date_to?: string }) {
  return useQuery({
    queryKey: ['supplier-statement', supplierId, params],
    queryFn: async () => {
      const res = await apiClient.get(`/finance/payables/statement/${supplierId}/`, { params })
      return res.data as StatementResult
    },
    enabled: !!supplierId,
  })
}

export function useApCashFlow(weeks = 12) {
  return useQuery({
    queryKey: ['ap-cash-flow', weeks],
    queryFn: async () => {
      const res = await apiClient.get('/finance/payables/cash-flow/', { params: { weeks } })
      return res.data
    },
  })
}

export function useApOverdue() {
  return useQuery({
    queryKey: ['ap-overdue'],
    queryFn: async () => {
      const res = await apiClient.get('/finance/payables/overdue/')
      return res.data as { invoices: OverdueInvoice[]; count: number }
    },
  })
}

export function usePaymentSchedule(weeks = 8) {
  return useQuery({
    queryKey: ['payment-schedule', weeks],
    queryFn: async () => {
      const res = await apiClient.get('/finance/payables/payment-schedule/', { params: { weeks } })
      return res.data as { schedule: any[]; total: string; count: number }
    },
  })
}
